import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { AlertCircle, ChevronDown, ChevronRight, Inbox, Search } from 'lucide-react';
import { selectUser } from '../store/authSlice';
import { payoutsApi } from '../api/payoutsApi';

/**
 * Payouts: what the owner paid each office and the staff under it over a date
 * range. Two views over the same data — grouped by office (expandable to staff)
 * or flattened by staff — toggled client-side. Net-negative payouts (clawback
 * exceeds payout) are shown in danger colour, never hidden.
 *
 * The backend call is explicit: the picker edits a DRAFT range; pressing
 * Search COMMITS it to {@code query}, and only a change to the committed query
 * fires the request. A period with no run returns empty, as expected, rather
 * than the page silently widening into one that has data.
 */
export default function PayoutsPage() {
    const user = useSelector(selectUser);
    const orgUuid = user?.organisationUuid;

    // draft = what the picker edits; query = what's actually been searched.
    const [draft, setDraft] = useState(() => defaultRange());
    const [query, setQuery] = useState(() => defaultRange());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [groupBy, setGroupBy] = useState('office');   // 'office' | 'staff' | 'type'
    const [expanded, setExpanded] = useState(() => new Set());

    // Fires only when the COMMITTED query changes — not on every keystroke.
    useEffect(() => {
        if (!orgUuid) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        payoutsApi
            .range(orgUuid, query.from, query.to)
            .then((d) => !cancelled && setData(d))
            .catch((err) => !cancelled && setError(extractMessage(err)))
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [orgUuid, query.from, query.to]);

    const offices = data?.offices ?? [];

    // Flattened staff list for the "by staff" view: every payee with its office,
    // highest net first.
    const staffRows = useMemo(() => {
        const rows = [];
        for (const o of offices) {
            for (const p of o.payees ?? []) {
                rows.push({ ...p, officeName: o.name });
            }
        }
        return rows.sort((a, b) => Number(b.net) - Number(a.net));
    }, [offices]);

    // "By type" buckets: every payee folded into Subcontractor vs Referrer,
    // each bucket keeping its member payees (with their office) so the row can
    // expand to show them — same pattern as the office view. Unknown/missing
    // types fall into an "Other" bucket so nothing silently disappears.
    const typeGroups = useMemo(() => {
        const acc = {
            SUBCONTRACTOR: { key: 'SUBCONTRACTOR', label: 'Subcontractor', upfront: 0, trail: 0, net: 0, payees: [] },
            REFERRER:      { key: 'REFERRER',      label: 'Referrer',      upfront: 0, trail: 0, net: 0, payees: [] },
            OTHER:         { key: 'OTHER',         label: 'Other',         upfront: 0, trail: 0, net: 0, payees: [] },
        };
        for (const p of staffRows) {
            const g = acc[p.type] ?? acc.OTHER;
            g.upfront += Number(p.upfront) || 0;
            g.trail   += Number(p.trail)   || 0;
            g.net     += Number(p.net)     || 0;
            g.payees.push(p);   // staffRows entries already carry officeName
        }
        // Members highest-net first within each bucket.
        return Object.values(acc)
            .filter((g) => g.payees.length > 0)
            .map((g) => ({ ...g, payees: g.payees.slice().sort((a, b) => Number(b.net) - Number(a.net)) }));
    }, [staffRows]);

    const toggleOffice = (name) =>
        setExpanded((prev) => {
            const next = new Set(prev);
            next.has(name) ? next.delete(name) : next.add(name);
            return next;
        });

    // Search: commit whatever's currently in the draft.
    const runSearch = () => {
        if (!draft.from || !draft.to || draft.from > draft.to) return;
        setQuery({ from: draft.from, to: draft.to });
    };

    const dirty = draft.from !== query.from || draft.to !== query.to;
    const invalidRange = !draft.from || !draft.to || draft.from > draft.to;

    return (
        <div className="px-8 py-8 max-w-5xl">
            <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
                <div>
                    <h2 className="font-display text-2xl font-semibold text-slate-900">
                        Payouts
                    </h2>
                    <p className="text-sm text-slate-500 mt-1">
                        What you paid each office and their staff over a period.
                    </p>
                </div>
                <RangePicker
                    draft={draft}
                    setDraft={setDraft}
                    onSearch={runSearch}
                    dirty={dirty}
                    invalid={invalidRange}
                    loading={loading}
                />
            </div>

            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2 text-sm text-red-700">
                    <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            <MetricCards data={data} loading={loading} />

            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="inline-flex border border-slate-300 rounded-md overflow-hidden">
                    <ToggleButton active={groupBy === 'office'} onClick={() => setGroupBy('office')}>
                        By office
                    </ToggleButton>
                    <ToggleButton active={groupBy === 'staff'} onClick={() => setGroupBy('staff')}>
                        By staff
                    </ToggleButton>
                    <ToggleButton active={groupBy === 'type'} onClick={() => setGroupBy('type')}>
                        By type
                    </ToggleButton>
                </div>
                <span className="text-xs text-slate-400">
                    Showing {query.from} → {query.to}
                </span>
            </div>

            <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full" style={{ tableLayout: 'fixed' }}>
                    <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                        <Th>{groupBy === 'office' ? 'Office / payee' : groupBy === 'type' ? 'Type / payee' : 'Payee'}</Th>
                        <Th align="right">Upfront</Th>
                        <Th align="right">Trail</Th>
                        <Th align="right">Net</Th>
                    </tr>
                    </thead>
                    <tbody>
                    {loading && (
                        <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-slate-400">Loading…</td></tr>
                    )}

                    {!loading && offices.length === 0 && (
                        <tr>
                            <td colSpan={4} className="px-4 py-10">
                                <div className="flex flex-col items-center gap-2 text-center">
                                    <Inbox size={20} className="text-slate-300" />
                                    <p className="text-sm text-slate-400">
                                        No payouts recorded for this period.
                                    </p>
                                </div>
                            </td>
                        </tr>
                    )}

                    {!loading && offices.length > 0 && groupBy === 'office' &&
                        offices.map((o) => (
                            <OfficeRows
                                key={o.name}
                                office={o}
                                expanded={expanded.has(o.name)}
                                onToggle={() => toggleOffice(o.name)}
                            />
                        ))}

                    {!loading && offices.length > 0 && groupBy === 'staff' &&
                        staffRows.map((p, i) => (
                            <StaffRow key={`${p.officeName}-${p.name}-${i}`} payee={p} />
                        ))}

                    {!loading && offices.length > 0 && groupBy === 'type' &&
                        typeGroups.map((g) => (
                            <TypeRows
                                key={g.key}
                                group={g}
                                expanded={expanded.has(`type:${g.key}`)}
                                onToggle={() => toggleOffice(`type:${g.key}`)}
                            />
                        ))}
                    </tbody>
                    {!loading && offices.length > 0 && (
                        <tfoot>
                        <tr className="border-t border-slate-200 bg-slate-50">
                            <td className="px-4 py-3 text-sm font-medium text-slate-900">Total</td>
                            <Money className="font-medium">{data.totalUpfront}</Money>
                            <Money className="font-medium">{data.totalTrail}</Money>
                            <Money className="font-medium">{data.totalNet}</Money>
                        </tr>
                        </tfoot>
                    )}
                </table>
            </div>

            <p className="text-xs text-slate-400 mt-2">
                Latest version of each period in range · expand a row to see its payees.
            </p>
        </div>
    );
}

// ── Pieces ──────────────────────────────────────────────────────────────────

function MetricCards({ data, loading }) {
    const cards = [
        { label: 'Total paid', value: data?.totalNet, danger: false },
        { label: 'Upfront', value: data?.totalUpfront, danger: false },
        { label: 'Trail', value: data?.totalTrail, danger: false },
        { label: 'Clawback', value: negate(data?.totalClawback), danger: true },
        { label: 'Adhoc', value: data?.totalAdhoc, danger: false },
    ];
    return (
        <div className="grid gap-3 mb-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
            {cards.map((c) => (
                <div key={c.label} className="bg-slate-50 rounded-md p-4">
                    <div className="text-[13px] text-slate-500">{c.label}</div>
                    <div className={`text-2xl font-semibold mt-1 ${c.danger ? 'text-red-600' : 'text-slate-900'}`}>
                        {loading ? '—' : formatMoney(c.value)}
                    </div>
                </div>
            ))}
        </div>
    );
}

function OfficeRows({ office, expanded, onToggle }) {
    const hasPayees = (office.payees ?? []).length > 0;
    return (
        <>
            <tr
                onClick={hasPayees ? onToggle : undefined}
                className={`border-b border-slate-100 bg-slate-50/60 ${hasPayees ? 'cursor-pointer hover:bg-slate-100' : ''}`}
            >
                <td className="px-4 py-3 text-sm font-medium text-slate-900">
                    {hasPayees && (expanded
                        ? <ChevronDown size={15} className="inline text-slate-400 mr-1 -mt-0.5" />
                        : <ChevronRight size={15} className="inline text-slate-400 mr-1 -mt-0.5" />)}
                    {office.name}
                </td>
                <Money className="font-medium">{office.upfront}</Money>
                <Money className="font-medium">{office.trail}</Money>
                <Money className="font-medium" net>{office.net}</Money>
            </tr>
            {expanded && hasPayees && (office.payees ?? []).map((p, i) => (
                <tr key={`${office.name}-${p.name}-${i}`} className="border-b border-slate-100">
                    <td className="pl-10 pr-4 py-2.5 text-sm text-slate-600">{p.name}<TypePill type={p.type} /></td>
                    <Money>{p.upfront}</Money>
                    <Money>{p.trail}</Money>
                    <Money net>{p.net}</Money>
                </tr>
            ))}
        </>
    );
}

function TypeRows({ group, expanded, onToggle }) {
    const hasPayees = group.payees.length > 0;
    return (
        <>
            <tr
                onClick={hasPayees ? onToggle : undefined}
                className={`border-b border-slate-100 bg-slate-50/60 ${hasPayees ? 'cursor-pointer hover:bg-slate-100' : ''}`}
            >
                <td className="px-4 py-3 text-sm font-medium text-slate-900">
                    {hasPayees && (expanded
                        ? <ChevronDown size={15} className="inline text-slate-400 mr-1 -mt-0.5" />
                        : <ChevronRight size={15} className="inline text-slate-400 mr-1 -mt-0.5" />)}
                    {group.label}
                    <span className="text-slate-400 font-normal"> · {group.payees.length} {group.payees.length === 1 ? 'payee' : 'payees'}</span>
                </td>
                <Money className="font-medium">{group.upfront}</Money>
                <Money className="font-medium">{group.trail}</Money>
                <Money className="font-medium" net>{group.net}</Money>
            </tr>
            {expanded && hasPayees && group.payees.map((p, i) => (
                <tr key={`${group.key}-${p.officeName}-${p.name}-${i}`} className="border-b border-slate-100">
                    <td className="pl-10 pr-4 py-2.5 text-sm text-slate-600">
                        {p.name}
                        <span className="text-slate-400"> · {p.officeName}</span>
                    </td>
                    <Money>{p.upfront}</Money>
                    <Money>{p.trail}</Money>
                    <Money net>{p.net}</Money>
                </tr>
            ))}
        </>
    );
}

function StaffRow({ payee }) {
    return (
        <tr className="border-b border-slate-100 last:border-b-0">
            <td className="px-4 py-3 text-sm">
                <span className="text-slate-900">{payee.name}</span>
                <TypePill type={payee.type} />
                <span className="text-slate-400"> · {payee.officeName}</span>
            </td>
            <Money>{payee.upfront}</Money>
            <Money>{payee.trail}</Money>
            <Money net>{payee.net}</Money>
        </tr>
    );
}

function TypePill({ type }) {
    if (!type) return null;
    const styles = {
        SUBCONTRACTOR: 'bg-indigo-50 text-indigo-700 border-indigo-200',
        REFERRER: 'bg-teal-50 text-teal-700 border-teal-200',
    };
    const labels = {
        SUBCONTRACTOR: 'Subcontractor',
        REFERRER: 'Referrer',
    };
    return (
        <span className={`ml-2 inline-flex px-1.5 py-0.5 text-[10px] font-medium border rounded-full align-middle ${styles[type] ?? 'bg-slate-50 text-slate-500 border-slate-200'}`}>
            {labels[type] ?? type}
        </span>
    );
}

function Money({ children, className = '', net = false }) {
    const n = Number(children);
    const danger = net && n < 0;
    return (
        <td className={`px-4 py-2.5 text-sm text-right tabular-nums ${danger ? 'text-red-600' : 'text-slate-700'} ${className}`}>
            {formatMoney(children)}
        </td>
    );
}

/**
 * From/To date range + Search. Editing the inputs only updates the draft;
 * nothing hits the backend until Search is pressed (or Enter in an input).
 * The button highlights when the draft differs from what's currently shown.
 */
function RangePicker({ draft, setDraft, onSearch, dirty, invalid, loading }) {
    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !invalid) onSearch();
    };
    return (
        <div className="flex items-center gap-2 flex-wrap">
            <input
                type="date"
                value={draft.from}
                onChange={(e) => setDraft({ ...draft, from: e.target.value })}
                onKeyDown={onKeyDown}
                className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white"
            />
            <span className="text-slate-400 text-sm">–</span>
            <input
                type="date"
                value={draft.to}
                onChange={(e) => setDraft({ ...draft, to: e.target.value })}
                onKeyDown={onKeyDown}
                className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white"
            />
            <button
                onClick={onSearch}
                disabled={invalid || loading}
                title={invalid ? 'Pick a valid date range first' : 'Search this range'}
                className={[
                    'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-colors disabled:opacity-50',
                    dirty
                        ? 'bg-brand-600 text-white hover:bg-brand-700'
                        : 'border border-slate-300 text-slate-600 hover:bg-slate-50',
                ].join(' ')}
            >
                <Search size={14} />
                {loading ? 'Searching…' : 'Search'}
            </button>
        </div>
    );
}

function ToggleButton({ active, onClick, children }) {
    return (
        <button
            onClick={onClick}
            className={`px-4 py-1.5 text-sm ${active ? 'bg-slate-100 font-medium text-slate-900' : 'bg-white text-slate-500 hover:text-slate-700'}`}
        >
            {children}
        </button>
    );
}

function Th({ children, align = 'left' }) {
    return (
        <th className={`px-4 py-2.5 text-xs font-medium text-slate-500 uppercase tracking-wider ${align === 'right' ? 'text-right' : 'text-left'}`}>
            {children}
        </th>
    );
}

// ── Date range helpers ────────────────────────────────────────────────────

// Local-date ISO (yyyy-MM-dd). NOT toISOString() — that converts to UTC and,
// east of Greenwich (e.g. Melbourne UTC+10/11), shifts local midnight back to
// the previous calendar day, producing off-by-one boundaries like 2026-01-30.
function iso(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/** Default: the current calendar month, first day → last day (local time). */
function defaultRange() {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();   // 0-based
    return { from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)) };
}

// ── Money formatting ───────────────────────────────────────────────────────

function formatMoney(v) {
    if (v == null) return '—';
    const n = Number(v);
    if (Number.isNaN(n)) return '—';
    const abs = Math.abs(n).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
    return n < 0 ? `−${abs}` : abs;
}

function negate(v) {
    if (v == null) return v;
    const n = Number(v);
    return n > 0 ? -n : n;
}

function extractMessage(err) {
    return err?.response?.data?.message || err?.message || 'Could not load payouts';
}